import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin'
import { useParams } from '@tanstack/react-router'
import {
  EmptyState,
  LoadingState,
  TypeaheadAvatar,
  TypeaheadDropdownContainer,
  TypeaheadDropdownHeader,
  TypeaheadInfo,
  TypeaheadOption,
  TypeaheadPrimaryText,
  TypeaheadScrollArea,
  TypeaheadSecondaryText,
} from 'features/richText/components/TypeaheadDropdownStyles'
import { DEFAULT_SEARCHABLE_SLASH_TYPES } from 'features/richText/constants'
import { createSlashCommandNode } from 'features/richText/nodes/SlashCommandNode'
import { useRolodexEntries } from 'features/rolodex'
import {
  CategoryFilters,
  getEntryTypeDisplayName,
  useGlobalSearch,
} from 'features/search'
import { $createTextNode, TextNode } from 'lexical'
import React, { useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { GeckoEntry, RolodexEntryType } from 'types'

const TRIGGER_CHAR = '/'

class SlashCommandTypeaheadOption extends MenuOption {
  entry: GeckoEntry

  constructor(entry: GeckoEntry) {
    const name = (entry.data?.name as string | undefined) || entry.id
    super(name)
    this.entry = entry
  }
}

interface SlashCommandPluginProps {
  /**
   * Maximum number of system suggestions to show
   */
  maxSuggestions?: number
  /**
   * Entry types that can be searched and inserted via slash commands
   */
  searchableTypes?: RolodexEntryType[]
}

export const SlashCommandPlugin: React.FC<SlashCommandPluginProps> = ({
  maxSuggestions = 10,
  searchableTypes = DEFAULT_SEARCHABLE_SLASH_TYPES,
}) => {
  const [queryString, setQueryString] = useState<string | null>(null)

  const { siteID } = useParams({ strict: false }) as { siteID?: string }
  const { getSite } = useRolodexEntries(
    siteID ? { ids: [siteID], type: [RolodexEntryType.SITE] } : undefined
  )

  const orgID = useMemo(() => {
    if (!siteID) return undefined
    const site = getSite(siteID)
    return site?.tags.organization
  }, [siteID, getSite])

  const { filteredResults, isLoading, counts, activeFilters, toggleFilter } =
    useGlobalSearch(queryString || '', siteID, orgID, {
      searchableTypes,
      previewLimit: maxSuggestions,
      expandedLimit: maxSuggestions,
      debounceMs: 200,
      minSearchLength: 0,
    })

  const entries = useMemo(() => {
    return filteredResults.map((result) => result.entry)
  }, [filteredResults])

  const options = useMemo(() => {
    if (!queryString && entries.length === 0) return []
    return entries.map((entry) => new SlashCommandTypeaheadOption(entry))
  }, [queryString, entries])

  const onSelectOption = useCallback(
    (
      selectedOption: SlashCommandTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      const slashCommandNode = createSlashCommandNode(selectedOption.entry)
      if (nodeToReplace) {
        nodeToReplace.replace(slashCommandNode)
      }
      slashCommandNode.insertAfter($createTextNode(' '))
      closeMenu()
    },
    []
  )

  const checkForSlashMatch = useBasicTypeaheadTriggerMatch(TRIGGER_CHAR, {
    minLength: 0,
    allowWhitespace: true,
  })

  return (
    <LexicalTypeaheadMenuPlugin<SlashCommandTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForSlashMatch}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ) => {
        if (!anchorElementRef.current) return null

        const getHeaderText = () => {
          if (searchableTypes.length === 1) {
            return `Insert ${getEntryTypeDisplayName(searchableTypes[0])}`
          }
          return 'Insert Entry'
        }

        const getEntryIcon = (entryType: string) => {
          return entryType.charAt(0).toUpperCase()
        }

        const getEntryDisplayName = (entry: GeckoEntry) => {
          return (entry.data?.name as string | undefined) || entry.id
        }

        const getEntryType = (entry: GeckoEntry) => {
          return entry.type
        }

        return createPortal(
          <TypeaheadDropdownContainer $minWidth='20rem' $maxWidth='24rem'>
            <TypeaheadDropdownHeader>{getHeaderText()}</TypeaheadDropdownHeader>
            {searchableTypes.length > 1 && (
              <CategoryFilters
                counts={counts}
                activeFilters={activeFilters}
                onToggleFilter={toggleFilter}
              />
            )}
            <TypeaheadScrollArea>
              {isLoading ? (
                <LoadingState>Loading entries...</LoadingState>
              ) : options.length === 0 ? (
                <EmptyState>No entries found</EmptyState>
              ) : (
                options.map((option, index) => (
                  <TypeaheadOption
                    key={option.entry.id}
                    ref={option.setRefElement}
                    $isSelected={selectedIndex === index}
                    onClick={() => {
                      setHighlightedIndex(index)
                      selectOptionAndCleanUp(option)
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}>
                    <TypeaheadAvatar $shape='rounded'>
                      {getEntryIcon(option.entry.type)}
                    </TypeaheadAvatar>
                    <TypeaheadInfo>
                      <TypeaheadPrimaryText>
                        {getEntryDisplayName(option.entry)}
                      </TypeaheadPrimaryText>
                      <TypeaheadSecondaryText>
                        {getEntryType(option.entry)}
                      </TypeaheadSecondaryText>
                    </TypeaheadInfo>
                  </TypeaheadOption>
                ))
              )}
            </TypeaheadScrollArea>
          </TypeaheadDropdownContainer>,
          anchorElementRef.current
        )
      }}
    />
  )
}
